import { Component } from 'react';
import { RootState } from '../redux/models';
import { selectRouteParams } from '../redux/selectors';
import { connect } from 'react-redux';
import WebAudioAPISound from './WebApiSound'; // Import the WebAudioAPISound class
import { BackgroundMusicSourceType, EvaluatedBackgroundMusicOptions } from '@videosmart/player-template';
import { selectBackgroundMusic } from '../redux/selectors/selectBackgroundMusic';
import { getAmazonS3Url } from '../utils';
import { EndpointActions } from '../redux/actions';

interface BackgroundMusicProps {
	isPlaying: boolean;
	volume: number;
	isMuted: boolean;
	isLoading: boolean;
	isEnded: boolean;
	playerId?: string;
	isPaused: boolean;
	backgroundMusic?: EvaluatedBackgroundMusicOptions;
	signAsset: typeof EndpointActions.actionCreators.signAsset;
}

class BackgroundMusic extends Component<BackgroundMusicProps> {
	private audio: WebAudioAPISound | null = null; // Reference to WebAudioAPISound instance
	private initializeAudioPromise: Promise<void> | null = null;
	private isDisposed: boolean = false;
	private currentTimeCheckTimeout: number | null = null; // Timeout to check currentTime

	public async componentDidMount() {
		// Initialize WebAudioAPISound instance
		await this.InitializeAudio();
	}

	public async componentDidUpdate(prevProps: BackgroundMusicProps) {
		// Ensure audio is initialized
		await this.InitializeAudio();

		const { isPlaying, volume, isMuted, isPaused, isEnded } = this.props;

		if (!this.audio) return;

		// Keep the audio context alive when playing
		if (isEnded !== prevProps.isEnded && isEnded) {
			this.audio.keepAudioContextAlive(); // Call the method to keep the audio context alive
			this.startCurrentTimeCheck(); // Start checking currentTime
			return;
		}

		// Handle play/pause based on isPlaying and replay scene
		if (isPlaying !== prevProps.isPlaying || isEnded !== prevProps.isEnded) {
			if (isPlaying && !isEnded) {
				this.audio.play();
				if (this.currentTimeCheckTimeout) clearTimeout(this.currentTimeCheckTimeout);
			}
		}

		if (isPaused !== prevProps.isPaused) {
			if (isPaused) {
				this.fadeOut();
			}
		}

		// Handle volume changes
		if (volume !== prevProps.volume) this.audio.setVolume(volume); // Adjust volume or mute

		// Handle mute/unmute
		if (isMuted !== prevProps.isMuted) {
			if (isMuted) {
				this.audio.setVolume(0);
			} else if (!isPaused && isPlaying) {
				this.audio.setVolume(volume);
			}
		}
	}

	public componentWillUnmount() {
		this.isDisposed = true;

		// Stop audio and clean up
		if (this.audio) {
			this.audio.stop();
		}
		// Clear the timeout
		if (this.currentTimeCheckTimeout) {
			clearTimeout(this.currentTimeCheckTimeout);
		}
	}

	public render() {
		return null; // No UI needed for background music
	}

	private async InitializeAudio() {
		if (this.audio || this.isDisposed) return;
		if (this.initializeAudioPromise) return this.initializeAudioPromise;

		this.initializeAudioPromise = (async () => {
			const backgroundMusic = this.props.backgroundMusic;
			const url = await this.resolveBackgroundMusicUrl(backgroundMusic);

			// Component may have unmounted while we were awaiting URL resolution.
			if (this.audio || this.isDisposed) return;
			if (!url) return;

			const createdAudio = new WebAudioAPISound(url, {
				loop: backgroundMusic?.loop || true,
				maxVolume: backgroundMusic?.maxVolume,
			});

			if (this.isDisposed) {
				createdAudio.stop();
				return;
			}

			// Only assign if still not initialized (another init may have won the race).
			if (!this.audio) this.audio = createdAudio;
			else createdAudio.stop();
		})();

		try {
			await this.initializeAudioPromise;
		} finally {
			this.initializeAudioPromise = null;
		}
	}

	private async resolveBackgroundMusicUrl(backgroundMusic?: EvaluatedBackgroundMusicOptions): Promise<string | undefined> {
		if (!backgroundMusic || !backgroundMusic.source) return undefined;

		const backgroundMusicSource = backgroundMusic.source;
		let url: string | undefined;

		switch (backgroundMusicSource.type) {
			case BackgroundMusicSourceType.Url:
				url = backgroundMusicSource.url;
				break;
			case BackgroundMusicSourceType.S3:
				if (backgroundMusicSource.isPrivate) {
					const { url: signedUrl } = await this.props.signAsset({
						s3Key: backgroundMusicSource.s3Key,
						s3Bucket: backgroundMusicSource.s3Bucket,
						s3Region: backgroundMusicSource.s3Region,
					});
					url = signedUrl;
				} else {
					url = getAmazonS3Url(backgroundMusicSource.s3Bucket, backgroundMusicSource.s3Key);
				}
				break;
		}
		if (!url) throw new Error('Background music URL could not be determined.');
		return url;
	}

	private startCurrentTimeCheck() {
		// Clear any existing timeout
		if (this.currentTimeCheckTimeout) {
			clearTimeout(this.currentTimeCheckTimeout);
		}

		// Set a new timeout to check currentTime
		this.currentTimeCheckTimeout = window.setTimeout(() => {
			if (!this.audio) return;
			this.audio.fadeOut();
		}, 10000); // Check after 10 seconds
	}

	private fadeOut() {
		if (this.isDisposed) return;
		if (!this.audio) return;
		if (!this.props.isPaused) return;
		this.audio.fadeOut();
	}
}

const mapStateToProps = (state: RootState): Pick<BackgroundMusicProps, 'isPlaying' | 'volume' | 'isMuted' | 'isEnded' | 'isLoading' | 'playerId' | 'isPaused' | 'backgroundMusic' | 'signAsset'> => ({
	...selectRouteParams(state),
	isPlaying: state.video.isPlaying && state.data.isLoaded,
	volume: state.video.volume,
	isMuted: state.video.isMuted,
	isLoading: state.video.isLoading || state.data.isLoading,
	isEnded: state.video.isEnded,
	playerId: state.data.isLoaded ? state.data.playerId : undefined,
	isPaused: state.video.isPaused,
	backgroundMusic: state.data.isLoaded ? selectBackgroundMusic(state) : undefined,
	signAsset: EndpointActions.actionCreators.signAsset,
});

export default connect(mapStateToProps)(BackgroundMusic);
